import React from 'react';
import { motion } from 'framer-motion';
import {
  PhoneCall, MessageSquare, Mail, ArrowUpRight, CheckCircle2, Clock, Calendar,
} from 'lucide-react';
import {
  TEMP_META, effectiveTemp, computeNBA, NBA_TONE, computeSignals, SIGNAL_TONE,
  computePipelineStage, STAGE_META, fmtRel, fmtMoney, daysSince, daysUntil,
} from './helpers';

const STAGE_DOT = {
  blue: 'bg-blue-500', sky: 'bg-sky-500', cyan: 'bg-cyan-500', violet: 'bg-violet-500',
  indigo: 'bg-indigo-500', orange: 'bg-orange-500', amber: 'bg-amber-500',
  emerald: 'bg-emerald-500', slate: 'bg-slate-400',
};

const STATUS_LABELS = {
  new_lead: 'New Lead', contacted: 'Contacted', discovery: 'Discovery', educating: 'Educating',
  pre_qualifying: 'Pre-Qualifying', ready_to_proceed: 'Ready to Proceed',
  long_term_nurture: 'Long-term Nurture', lost: 'Lost', converted: 'Converted',
  moved_to_client_portal: 'Moved to Portal', graduated: 'Graduated',
};

export const LeadCard = ({ lead: l, onOpen, onComplete, compact = false }) => {
  const temp = TEMP_META[effectiveTemp(l)];
  const TempIcon = temp.icon;
  const nba = computeNBA(l);
  const NbaIcon = nba.icon;
  const tone = NBA_TONE[nba.tone] || NBA_TONE.slate;
  const signals = computeSignals(l);
  const stage = STAGE_META[computePipelineStage(l)];
  const est = fmtMoney(l.property_target_price_max || l.loan_amount || l.est_loan_amount);
  const ageDays = daysSince(l.created_at);
  const fu = l.next_follow_up_at || l.next_action_due;
  const overdue = fu && daysUntil(fu) < 0;
  const onKey = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } };

  return (
    <motion.div variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}>
      <div
        role="button" tabIndex={0} onClick={onOpen} onKeyDown={onKey}
        data-testid={`lead-card-${l.id}`}
        className="group relative w-full text-left cursor-pointer rounded-2xl sm:rounded-3xl bg-white border border-slate-200/80 hover:border-slate-300 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)] hover:shadow-[0_16px_44px_-20px_rgba(15,23,42,0.22)] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-rose-200 transition-[transform,box-shadow,border-color] p-4 sm:p-5"
      >
        {/* Temperature rail */}
        <span className={`absolute left-0 top-4 bottom-4 w-1 rounded-full ${temp.badge}`} aria-hidden />

        <div className="pl-2">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-base sm:text-lg font-black tracking-tight text-slate-950 truncate">{l.name || 'Unnamed lead'}</p>
                <span data-testid={`lead-temp-${l.id}`} className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ${temp.chip}`}>
                  <TempIcon className="w-3 h-3" /> {temp.label}
                </span>
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ring-slate-200 text-slate-600 bg-slate-50`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${STAGE_DOT[stage.color] || 'bg-slate-400'}`} /> {stage.label}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {l.lead_source || 'Unknown source'}
                {l.loan_purpose ? ` · ${l.loan_purpose}` : ''}
                {l.assigned_broker_name ? ` · ${l.assigned_broker_name}` : ''}
                {ageDays !== null ? ` · ${ageDays}d in CRM` : ''}
              </p>
            </div>
            {est && (
              <span className="flex-shrink-0 font-mono text-sm font-bold text-slate-900 bg-slate-100 rounded-lg px-2 py-1">{est}</span>
            )}
          </div>

          {/* Next Best Action — the focal element */}
          <div data-testid={`lead-nba-${l.id}`} className={`mt-3 flex items-center gap-2.5 rounded-xl px-3 py-2.5 ring-1 ${tone.ring} ${tone.bg}`}>
            <NbaIcon className={`w-4 h-4 flex-shrink-0 ${tone.icon}`} strokeWidth={2.4} />
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-400 leading-none">Next best action</p>
              <p className={`text-[13px] font-bold ${tone.text} truncate leading-tight mt-0.5`}>{nba.label}</p>
            </div>
            {nba.due && (
              <span className={`text-[10px] font-bold whitespace-nowrap ${overdue ? 'text-red-600' : 'text-slate-500'}`}>{fmtRel(nba.due)}</span>
            )}
            {onComplete && (
              <button
                type="button" data-testid={`lead-nba-complete-${l.id}`}
                onClick={(e) => { e.stopPropagation(); onComplete(l); }}
                className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-[11px] font-bold px-2.5 py-1 hover:bg-slate-700 transition-colors"
              >
                <CheckCircle2 className="w-3 h-3" /> <span className="hidden sm:inline">Done</span>
              </button>
            )}
          </div>

          {/* Signals */}
          {signals.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {signals.map((s) => (
                <span key={s.key} className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${SIGNAL_TONE[s.tone]}`}>
                  {s.label}
                </span>
              ))}
            </div>
          )}

          {/* Data + quick actions */}
          {!compact && (
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                {l.phone && <span className="inline-flex items-center gap-1"><PhoneCall className="w-3 h-3 text-slate-400" />{l.phone}</span>}
                {l.email && <span className="inline-flex items-center gap-1 truncate max-w-[180px]"><Mail className="w-3 h-3 text-slate-400" />{l.email}</span>}
                {l.last_contacted_at && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-slate-400" />Last {fmtRel(l.last_contacted_at)}</span>}
              </div>
              <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                {l.phone && <QA icon={PhoneCall} href={`tel:${l.phone}`} testid={`lead-qa-call-${l.id}`} tone="emerald" title="Call" />}
                {l.phone && <QA icon={MessageSquare} href={`sms:${l.phone}`} testid={`lead-qa-sms-${l.id}`} tone="violet" title="SMS" />}
                {l.email && <QA icon={Mail} href={`mailto:${l.email}`} testid={`lead-qa-email-${l.id}`} tone="blue" title="Email" />}
                <QA icon={ArrowUpRight} onClick={onOpen} testid={`lead-qa-open-${l.id}`} tone="slate" title="Open" />
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const QA = ({ icon: Icon, href, onClick, testid, tone, title }) => {
  const cls = {
    emerald: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    violet: 'bg-violet-50 text-violet-700 hover:bg-violet-100',
    blue: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
    slate: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
  }[tone];
  const common = `inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${cls}`;
  if (href) return <a href={href} onClick={(e) => e.stopPropagation()} data-testid={testid} title={title} className={common}><Icon className="w-3.5 h-3.5" strokeWidth={2.4} /></a>;
  return <button type="button" onClick={onClick} data-testid={testid} title={title} className={common}><Icon className="w-3.5 h-3.5" strokeWidth={2.4} /></button>;
};

export { STATUS_LABELS };
